import { usePostAdmins, usePutAdminsUserIdProfileimage } from '@/api/generated/admin/admin'
import { usePostProviders, usePutProvidersUserIdProfileimage } from '@/api/generated/doctor/doctor'
import type { AdminApiInContract, CreateDoctorAdminApiInContract } from '@/api/generated/pwrdbyQuickMDAdminsApi.schemas'

/**
 * Creates a clinic member (admin or provider) and, when a profile image was selected,
 * uploads it against the returned UserId — exactly the create → image-upload sequence the
 * old flow ran across `CREATE_NEW_MEMBER_*_SUCCESS` → the `newAdminMemberId`/`newProviderMemberId`
 * effects → `SET_*_PROFILE_IMG`. The page then navigates on success, mirroring the old
 * `pageState === 'success'` redirect.
 */

interface CreateAdminArgs {
  role: 'Admin'
  body: AdminApiInContract
  image: Blob | null
}

interface CreateProviderArgs {
  role: 'Provider'
  body: CreateDoctorAdminApiInContract
  image: Blob | null
}

export type CreateMemberArgs = CreateAdminArgs | CreateProviderArgs

export const useCreateMember = () => {
  const { mutateAsync: createAdmin, isPending: creatingAdmin } = usePostAdmins()
  const { mutateAsync: createProvider, isPending: creatingProvider } = usePostProviders()
  const { mutateAsync: uploadAdminImage, isPending: uploadingAdminImage } = usePutAdminsUserIdProfileimage()
  const { mutateAsync: uploadProviderImage, isPending: uploadingProviderImage } = usePutProvidersUserIdProfileimage()

  const createMember = async (args: CreateMemberArgs): Promise<void> => {
    if (args.role === 'Admin') {
      const created = await createAdmin({ data: args.body })
      if (args.image) {
        const userId = created.UserId ?? ''
        if (userId) {
          await uploadAdminImage({ userId, data: { image: args.image } })
        }
      }
      return
    }

    const created = await createProvider({ data: args.body })
    if (args.image) {
      const userId = created.UserId ?? ''
      if (userId) {
        await uploadProviderImage({ userId, data: { image: args.image } })
      }
    }
  }

  return {
    createMember,
    isPending: creatingAdmin || creatingProvider || uploadingAdminImage || uploadingProviderImage,
  }
}
